import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { useAuth } from '../../contexts/AuthContext';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Material {
  id: string;
  title: string;
  description: string;
  category: string;
  grade_level: string;
  price: number;
  page_count: number;
  is_active: boolean;
  created_at: string;
}

export default function AdminPage() {
  const navigate = useNavigate();
  const { user, signOut, role } = useAuth();
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<Material | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '代數',
    grade_level: '小一',
    price: 0,
    page_count: 0,
    thumbnail_url: '',
    preview_file_url: '',
    full_file_url: '',
  });
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [uploadingPreview, setUploadingPreview] = useState(false);
  const [uploadingFull, setUploadingFull] = useState(false);

  const grades = [
    { value: 'all', label: '全部年級' },
    { value: '幼稚園', label: '幼稚園' },
    { value: '小一', label: '小一' },
    { value: '小二', label: '小二' },
    { value: '小三', label: '小三' },
    { value: '小四', label: '小四' },
    { value: '小五', label: '小五' },
    { value: '小六', label: '小六' },
    { value: '中一', label: '中一' },
    { value: '中二', label: '中二' },
    { value: '中三', label: '中三' },
    { value: '中四', label: '中四' },
    { value: '中五', label: '中五' },
    { value: '中六', label: '中六' },
  ];

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // 檢查是否為行政人員
    if (role && role !== 'admin') {
      alert('您沒有權限訪問此頁面');
      navigate('/');
      return;
    }

    fetchMaterials();
  }, [user, role, navigate]);

  const fetchMaterials = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('materials')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setMaterials(data || []);
    } catch (error) {
      console.error('獲取教材失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingMaterial) {
        // 更新教材
        const { error } = await supabase
          .from('materials')
          .update({
            title: formData.title,
            description: formData.description,
            category: formData.category,
            grade_level: formData.grade_level,
            price: formData.price,
            page_count: formData.page_count,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingMaterial.id);

        if (error) throw error;
        alert('教材更新成功！');
      } else {
        // 新增教材 - 使用預設圖片
        const { error } = await supabase
          .from('materials')
          .insert({
            title: formData.title,
            description: formData.description,
            category: formData.category,
            grade_level: formData.grade_level,
            price: formData.price,
            page_count: formData.page_count,
            thumbnail_url: 'https://public.readdy.ai/ai/img_res/8377b02ed2ec032e208c5a57ceb44ed0.jpg',
            file_size: Math.floor(Math.random() * 10000000) + 1000000,
          });

        if (error) throw error;
        alert('教材新增成功！');
      }

      setShowAddModal(false);
      setEditingMaterial(null);
      resetForm();
      fetchMaterials();
    } catch (error) {
      console.error('操作失敗:', error);
      alert('操作失敗，請稍後再試');
    }
  };

  const handleEdit = (material: Material) => {
    setEditingMaterial(material);
    setFormData({
      title: material.title,
      description: material.description,
      category: material.category,
      grade_level: material.grade_level,
      price: material.price,
      page_count: material.page_count,
      thumbnail_url: '',
      preview_file_url: '',
      full_file_url: '',
    });
    setShowAddModal(true);
  };

  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('materials')
        .update({ is_active: !currentStatus })
        .eq('id', id);

      if (error) throw error;
      fetchMaterials();
    } catch (error) {
      console.error('更新狀態失敗:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: '代數',
      grade_level: '小一',
      price: 0,
      page_count: 0,
      thumbnail_url: '',
      preview_file_url: '',
      full_file_url: '',
    });
  };

  const handleThumbnailUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 檢查檔案類型
    if (!file.type.startsWith('image/')) {
      alert('請上傳圖片檔案');
      return;
    }

    // 檢查檔案大小（限制 5MB）
    if (file.size > 5 * 1024 * 1024) {
      alert('圖片大小不能超過 5MB');
      return;
    }

    setUploadingThumbnail(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `thumbnails/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('materials')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('materials')
        .getPublicUrl(filePath);

      setFormData({ ...formData, thumbnail_url: publicUrl });
      alert('圖片上傳成功！');
    } catch (error) {
      console.error('上傳圖片失敗:', error);
      alert('上傳圖片失敗，請稍後再試');
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handlePreviewUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 檢查檔案類型
    if (file.type !== 'application/pdf') {
      alert('請上傳 PDF 檔案');
      return;
    }

    // 檢查檔案大小（限制 10MB）
    if (file.size > 10 * 1024 * 1024) {
      alert('預覽檔案大小不能超過 10MB');
      return;
    }

    setUploadingPreview(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `previews/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('materials')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('materials')
        .getPublicUrl(filePath);

      setFormData({ ...formData, preview_file_url: publicUrl });
      alert('預覽檔案上傳成功！');
    } catch (error) {
      console.error('上傳預覽檔案失敗:', error);
      alert('上傳預覽檔案失敗，請稍後再試');
    } finally {
      setUploadingPreview(false);
    }
  };

  const handleFullFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 檢查檔案類型
    if (file.type !== 'application/pdf') {
      alert('請上傳 PDF 檔案');
      return;
    }

    // 檢查檔案大小（限制 50MB）
    if (file.size > 50 * 1024 * 1024) {
      alert('完整檔案大小不能超過 50MB');
      return;
    }

    setUploadingFull(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `full/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('materials')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('materials')
        .getPublicUrl(filePath);

      setFormData({ ...formData, full_file_url: publicUrl });
      alert('完整檔案上傳成功！');
    } catch (error) {
      console.error('上傳完整檔案失敗:', error);
      alert('上傳完整檔案失敗，請稍後再試');
    } finally {
      setUploadingFull(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <img src="https://public.readdy.ai/ai/img_res/46c4b423-33ec-4c59-b54c-70c60984f453.png" alt="Logo" className="h-10 w-10 object-contain" />
              <span className="text-xl font-bold text-gray-900">管理後台</span>
            </Link>
            <div className="flex items-center space-x-4">
              <Link to="/materials" className="text-gray-700 hover:text-orange-500 transition-colors font-medium">
                教材商店
              </Link>
              <Link to="/homework" className="text-gray-700 hover:text-orange-500 transition-colors font-medium">
                作業上傳
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">教材管理</h1>
            <p className="text-gray-600">管理所有數學教材與課程內容</p>
          </div>
          <button
            onClick={() => {
              setEditingMaterial(null);
              resetForm();
              setShowAddModal(true);
            }}
            className="px-6 py-3 bg-blue-900 text-white rounded-full font-semibold hover:bg-blue-800 transition-colors flex items-center space-x-2 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line text-xl"></i>
            <span>新增教材</span>
          </button>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 text-sm">總教材數</span>
              <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded-lg">
                <i className="ri-book-line text-xl text-blue-600"></i>
              </div>
            </div>
            <div className="text-3xl font-bold text-gray-900">{materials.length}</div>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 text-sm">已啟用</span>
              <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-lg">
                <i className="ri-checkbox-circle-line text-xl text-green-600"></i>
              </div>
            </div>
            <div className="text-3xl font-bold text-gray-900">
              {materials.filter(m => m.is_active).length}
            </div>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 text-sm">已停用</span>
              <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg">
                <i className="ri-close-circle-line text-xl text-gray-600"></i>
              </div>
            </div>
            <div className="text-3xl font-bold text-gray-900">
              {materials.filter(m => !m.is_active).length}
            </div>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 text-sm">總收入</span>
              <div className="w-10 h-10 flex items-center justify-center bg-orange-100 rounded-lg">
                <i className="ri-money-dollar-circle-line text-xl text-orange-600"></i>
              </div>
            </div>
            <div className="text-3xl font-bold text-gray-900">
              HK$ {materials.reduce((sum, m) => sum + m.price, 0).toLocaleString()}
            </div>
          </div>
        </div>

        {/* Materials Table */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">教材名稱</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">分類</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">年級</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">價格</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">頁數</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">狀態</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900"></div>
                      </div>
                    </td>
                  </tr>
                ) : materials.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                      尚無教材資料
                    </td>
                  </tr>
                ) : (
                  materials.map((material) => (
                    <tr key={material.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{material.title}</div>
                        <div className="text-sm text-gray-500 line-clamp-1">{material.description}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-md text-xs font-medium">
                          {material.category}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-md text-xs font-medium">
                          {material.grade_level}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-900 font-medium">
                        HK$ {material.price}
                      </td>
                      <td className="px-6 py-4 text-gray-600">
                        {material.page_count} 頁
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleToggleActive(material.id, material.is_active)}
                          className={`px-3 py-1 rounded-full text-xs font-medium cursor-pointer ${
                            material.is_active
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {material.is_active ? '已啟用' : '已停用'}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleEdit(material)}
                          className="text-blue-600 hover:text-blue-700 font-medium text-sm cursor-pointer"
                        >
                          編輯
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-2xl w-full p-8 space-y-6 my-8">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingMaterial ? '編輯教材' : '新增教材'}
              </h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingMaterial(null);
                  resetForm();
                }}
                className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  教材名稱 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="例如：高中代數完整教材"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  教材描述 <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="詳細描述教材內容..."
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  required
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    分類 <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm cursor-pointer"
                    required
                  >
                    <option value="代數">代數</option>
                    <option value="幾何">幾何</option>
                    <option value="微積分">微積分</option>
                    <option value="統計">統計</option>
                    <option value="三角函數">三角函數</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    年級 <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.grade_level}
                    onChange={(e) => setFormData({ ...formData, grade_level: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm cursor-pointer"
                    required
                  >
                    <option value="幼稚園">幼稚園</option>
                    <option value="小一">小一</option>
                    <option value="小二">小二</option>
                    <option value="小三">小三</option>
                    <option value="小四">小四</option>
                    <option value="小五">小五</option>
                    <option value="小六">小六</option>
                    <option value="中一">中一</option>
                    <option value="中二">中二</option>
                    <option value="中三">中三</option>
                    <option value="中四">中四</option>
                    <option value="中五">中五</option>
                    <option value="中六">中六</option>
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    價格（港幣）
                  </label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                    placeholder="例如：299"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    頁數 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.page_count}
                    onChange={(e) => setFormData({ ...formData, page_count: Number(e.target.value) })}
                    placeholder="50"
                    min="1"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    required
                  />
                </div>
              </div>

              {/* 教材圖片 - 暫時關閉上傳功能 */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-blue-600 rounded-lg flex-shrink-0">
                    <i className="ri-information-line text-xl text-white"></i>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">教材圖片說明</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      目前系統會自動為新教材生成預設圖片。如需自訂圖片，請稍後使用編輯功能更新。
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingMaterial(null);
                    resetForm();
                  }}
                  className="px-6 py-3 bg-gray-200 text-gray-900 rounded-full font-semibold hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer"
                >
                  取消
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-blue-900 text-white rounded-full font-semibold hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {editingMaterial ? '更新教材' : '新增教材'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
